// Unpublished Work © 2022-2024 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import {Route, Switch, useLocation} from 'react-router-dom';
import ErrorView from 'Ui/components/dashboard/error-view';
import {isAuthorized} from 'Common/utils/authorization-handler';
import {WORKBOARD_DRAFTS, WORKBOARD_TEMPLATES, WORKBOARD, WORKBOARD_FORM, WORKBOARD_TEMPLATES_FORM} from 'Common/constants/routes';
import SettingsDialog from 'Ui/components/settings/settings-dialog';
import {getAvailableSettings} from 'Utils/settings-utils';
import {fetchEffectData} from 'Utils/react-utils';
import {BASE_ROUTES} from 'Ui/constants/routes/base-route-config';

const ROUTES = [
    {
        ...BASE_ROUTES[WORKBOARD_DRAFTS]
    },
    {
        ...BASE_ROUTES[WORKBOARD_TEMPLATES_FORM],
        exact: true
    },
    {
        ...BASE_ROUTES[WORKBOARD_TEMPLATES]
    },
    {
        ...BASE_ROUTES[WORKBOARD_FORM],
        exact: true
    },
    {
        ...BASE_ROUTES[WORKBOARD]
    }
];

function createLaborSettingsDialogRoute(settings) {
    const cleanPathname = useLocation().pathname.split('/settings')[0];
    const laborTile = settings.find((tile) => tile.tileId === 'Labor');

    if (!laborTile) {
        return null;
    }

    const lowerCaseTileId = laborTile.tileId.toLowerCase();
    const basePath = `${cleanPathname}/settings/${lowerCaseTileId}`;

    return (
        <Route
            key={laborTile.tileId}
            path={[`${basePath}/:tab`, basePath]}
        >
            <SettingsDialog
                basePath={`${cleanPathname}`}
                tileId={`settings/${lowerCaseTileId}`}
                {...laborTile.dialogProps}
            />
        </Route>
    );
}

function Labor(props) {
    const {
        translations,
        appKeys,
        membership,
        myJdPermissions,
        permissions
    } = props;

    const [availableSettings, setAvailableSettings] = React.useState([]);

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        const settings = await getAvailableSettings(translations, membership, appKeys, permissions, myJdPermissions);

        if (isMounted()) {
            setAvailableSettings(settings);
        }
    }), [membership, appKeys]);

    return (
        <>
            <Switch>
                {
                    ROUTES.map(({
                        component,
                        path,
                        toggle,
                        myJdPermissions,
                        exact
                    }) => {
                        const showComponent = isAuthorized({
                            toggle,
                            myJdPermissions
                        }, {
                            ...props,
                            isMigrated: membership.isMigrated
                        });
                        const Component = showComponent ? component : ErrorView;

                        return (
                            <Route
                                exact={exact}
                                key={path}
                                path={path}
                            >
                                <Component translations={translations}/>
                            </Route>
                        );
                    })
                }
            </Switch>
            {
                createLaborSettingsDialogRoute(availableSettings)
            }
        </>
    );
}

Labor.propTypes = {
    appKeys: PropTypes.appKeys,
    isgToggles: PropTypes.object,
    membership: PropTypes.object,
    myJdPermissions: PropTypes.myJdPermissions,
    permissions: PropTypes.legacyPermissions,
    superUser: PropTypes.bool,
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        appKeys: state.account.appKeys,
        superUser: state.account.superUser,
        membership: state.membership,
        myJdPermissions: state.account.myJdPermissions,
        permissions: state.account.permissions,
        isgToggles: state.account.isgToggles
    };
}

export default connect(mapStateToProps)(Labor);
